import { ChatVersion } from 'app/copied/constants/common';
import { ChatModuleType } from 'app/copied/models/ChatModules';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useAutoOpenDelay from 'app/hooks/useAutoOpenDelay';
import useModule from 'app/hooks/useModule';
import useUiConfig from 'app/hooks/useUiConfig';
import useWebchatVersion from 'app/hooks/useWebchatVersion';
import { ContactFormLinkLocation } from 'app/modules/contact-form/stores/ContactFormStore';
import { useContactFormState } from 'app/modules/contact-form/stores/useContactFormState';
import useLinks from 'app/modules/tailor/hooks/useLinks';
import { ChatFormItem, ChatFormItemType, ChatItemForm, ChatItemMessage, ChatItemType } from 'app/services/chatBot';
import { stripHTML } from 'app/utils/stripHTML';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useVirtualAssistantStore } from '../store/virtualAssistantStore';
import { ChatHandlerParams } from '../types/chat';
import useVirtualAssistantTracking from './useVirtualAssistantTracking';

export default function useVirtualAssistant() {
  const {
    startVirtualAssistant,
    chatItems,
    resetVirtualAssistant,
    typingAreaAvailable,
    isTyping,
    userResponsePresent,
    sendAnswer,
    sendMessage,
    dismissQuestion,
    isLoadingVirtualAssistant,
    latestAnswerResponse,
    showTypingArea,
    displayInitialMessage,
    resetScrollToBottomAfterInit,
    lastInitItemId,
    isConvAiActive,
  } = useVirtualAssistantStore(
    useShallow((state) => ({
      startVirtualAssistant: state.startVirtualAssistant,
      chatItems: state.chatItems,
      resetVirtualAssistant: state.resetVirtualAssistant,
      typingAreaAvailable: state.typingAreaAvailable,
      isTyping: state.isTyping,
      userResponsePresent: state.userResponsePresent,
      sendAnswer: state.sendAnswer,
      sendMessage: state.sendMessage,
      dismissQuestion: state.dismissQuestion,
      isLoadingVirtualAssistant: state.isLoadingVirtualAssistant,
      latestAnswerResponse: state.latestAnswerResponse,
      showTypingArea: state.showTypingArea,
      displayInitialMessage: state.displayInitialMessage,
      resetScrollToBottomAfterInit: state.resetScrollToBottomAfterInit,
      lastInitItemId: state.lastInitItemId,
      isConvAiActive: state.isConvAiActive,
    }))
  );

  const { openLink } = useLinks();
  const { captureChatOptionPresented, captureLinkClicked, capturePageview } = useVirtualAssistantTracking();
  const autoOpenDelay = useAutoOpenDelay();
  const { action, pillText, searchQuery } = useParams();
  const { setOpenedThrough } = useContactFormState();
  const [userMessage, setUserMessage] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { data: helpCenter } = useFetchHelpCenter();
  const webchatVersion = useWebchatVersion();
  const vaModule = useModule(ChatModuleType.VirtualAssistant);
  const uiConfig = useUiConfig();

  const visibleChatItems = useMemo(
    () =>
      chatItems.filter(
        (item) =>
          item.visible &&
          [
            ChatItemType.MESSAGE,
            ChatItemType.FORM,
            ChatItemType.KB_ENTRY,
            ChatItemType.DISCLAIMER_MESSAGE,
            ChatItemType.REDIRECT_LINK,
            ChatItemType.TRANSFER_TO_LIVECHAT,
            ChatItemType.TRANSFER_TO_VA,
            ChatItemType.LOCATION_RESULTS,
            ChatItemType.CONTACT_FORM,
            ChatItemType.STATIC_IMAGE,
            ChatItemType.VIDEO,
            ChatItemType.FEEDBACK,
          ].includes(item.type)
      ),
    [chatItems]
  );
  const isConvoAiEnabled = !!vaModule?.va_convo_ai_enabled;

  const handleContentClick = (
    e: React.MouseEvent,
    chatItem: ChatItemMessage,
    linkLocation?: ContactFormLinkLocation
  ) => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    const href = target?.closest('a')?.href;

    if (!href) return;
    captureLinkClicked(e, isConvAiActive, chatItem.aiResponse);
    openLink(href, { linkLocation });
  };

  const handleDisplayFormItem = (formItem: ChatFormItem, chatItemForm: ChatItemForm) => {
    if (formItem.item_type === ChatFormItemType.SINGE_CHOICE) {
      captureChatOptionPresented(formItem.choices, isConvAiActive, chatItemForm.aiResponse, chatItemForm.queryTyped);
    }
  };

  useEffect(() => {
    if (!helpCenter?.uuid) {
      return;
    }

    const state = useVirtualAssistantStore.getState();

    if (state.isLoadingVirtualAssistant || state.hasStartedVirtualAssistant) {
      return;
    }

    startVirtualAssistant(helpCenter?.uuid, {
      action,
      isV41: webchatVersion === ChatVersion.v4_1,
      isConvoAiEnabled,
      searchQuery,
    });

    capturePageview(autoOpenDelay);
  }, [helpCenter?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (action && pillText) {
      setOpenedThrough('pill');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, pillText]);

  useEffect(() => {
    return () => {
      resetVirtualAssistant();
    };
  }, [resetVirtualAssistant]);

  useEffect(() => {
    if (pillText) {
      displayInitialMessage(pillText);
    }
  }, [pillText, displayInitialMessage]);

  useEffect(() => {
    if (searchQuery) {
      displayInitialMessage(searchQuery);
    }
  }, [searchQuery, displayInitialMessage]);

  const handleAnswer = async (params: ChatHandlerParams) => {
    if (!helpCenter?.uuid) {
      return;
    }
    await sendAnswer(helpCenter?.uuid, params, uiConfig?.virtualAssistant?.showErrorMessage);
  };

  const handleDismiss = async (params: ChatHandlerParams) => {
    if (!helpCenter?.uuid) {
      return;
    }
    await dismissQuestion(helpCenter?.uuid, params);
  };

  const handleMessage = async (message: string) => {
    const sanitizedMessage = stripHTML(message);
    if (!helpCenter?.uuid) {
      return;
    }
    setUserMessage('');
    await sendMessage(helpCenter?.uuid, sanitizedMessage);
  };

  return {
    chatItems,
    isTyping,
    typingAreaAvailable,
    userMessage,
    userResponsePresent,
    isLoadingVirtualAssistant,
    visibleChatItems,
    chatContainerRef,
    setUserMessage,
    answerResponse: latestAnswerResponse,
    handleAnswer,
    handleDismiss,
    handleMessage,
    handleContentClick,
    handleDisplayFormItem,
    showTypingArea,
    resetScrollToBottomAfterInit,
    lastInitItemId,
  };
}
